import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { AuthFetchError } from '@e3dc-react/shell/Types/api'
import axios from 'axios'
import { useDispatch } from 'react-redux'
import { getActiveDeviceLinksOnly } from 'src/Elements/helperFunctions'
import { ApiResult } from 'src/Libs/api'
import { useInstallationDevices, useInstallationSubDevices } from 'src/Libs/flow/installation/hooks'
import { applyFlowSubDeviceNames } from 'src/Libs/flow/installation/utils'
import { StatusCodes } from 'src/Libs/http-status-codes'
import useDeviceLinks from 'src/Libs/installations/hooks/use-device-links'
import { DeviceLinkModel } from 'src/Libs/installations/models'
import { Installation } from 'src/Libs/installations/models/installation.model'
import { isFlowTheme } from 'src/Libs/utils'
import { useTypedSelector } from 'src/State/RootReducer'
import {
  addBatteries,
  setActiveStorageBattery,
  setInstallation,
  setLocation,
  setPowermeters,
  setStorageData,
  setStorageLoading,
} from 'src/State/Storage/StorageActions'
import { BatteryModel, PowermeterHardwareModel, StorageDataModel, StorageLocation } from 'src/State/Storage/StorageModel'

interface UseDashboardHelpersHook {
  loadPowermeters: (storageId: number) => Promise<void>
  loadPowermetersFromClients: (storageIds: string[]) => Promise<void>
  loadLocation: (storageId: number) => Promise<void>
  loadStorageData: (storageId: number, installationId: number) => Promise<void>
  getDeviceLinks: (installationId: number) => Promise<ApiResult<DeviceLinkModel[]>>
  isSubDevicesReady: boolean
}

const useDashboardHelpers = (): UseDashboardHelpersHook => {
  const installationId = useTypedSelector(state => state.storage.activeInstallation)
  const dispatch = useDispatch()
  const authFetch = useAuthFetchWithResponse()
  const { enqueueErrorStack } = useSnackbar()
  const { t } = useCustomTranslation()
  const { getDeviceLinks } = useDeviceLinks()
  const { data: devices } = useInstallationDevices(installationId, isFlowTheme)
  const emcId = devices?.emc?.id?.toString() ?? null
  const { data: subDevices, isSuccess: subDevicesSuccess } = useInstallationSubDevices(installationId, emcId, isFlowTheme)
  const isSubDevicesReady = isFlowTheme ? subDevicesSuccess : true

  const loadPowermeters = async (storageId: number): Promise<void> => {
    try {
      const response = await authFetch<PowermeterHardwareModel[]>({ path: `storages/${storageId}/hardware/powermeters` })
      if (response.status === StatusCodes.OK) {
        const powerMeterToUse = isFlowTheme ? applyFlowSubDeviceNames(response.data, subDevices) : response.data
        dispatch(setPowermeters(powerMeterToUse))
      }
    } catch (err) {
      if (!axios.isAxiosError(err)) throw err
      const error = err as AuthFetchError
      console.error(err)
      enqueueErrorStack(t('dashboard.errors.powermeters-hardware-fetch'), error.code, error.message)
    }
  }

  /**
   * This function fetch all powermeters from all clients and save them in redux store
   * this is only a Workaround until BE fixes (AF-3988).
   */
  const loadPowermetersFromClients = async (storageIds: string[]): Promise<void> => {
    try {
      const settledResults = await Promise.allSettled(
        storageIds.map(id =>
          authFetch<PowermeterHardwareModel[]>({
            path: `storages/${id}/hardware/powermeters`,
          })
        )
      )
      const clientsPowermeters = settledResults.reduce<PowermeterHardwareModel[]>((acc, result) => {
        if (result.status === 'fulfilled' && result.value.status === StatusCodes.OK) {
          return acc.concat(result.value.data ?? [])
        }
        return acc
      }, [])
      const powerMetersToUse = isFlowTheme ? applyFlowSubDeviceNames(clientsPowermeters, subDevices) : clientsPowermeters

      dispatch(setPowermeters(powerMetersToUse))
    } catch (err) {
      if (!axios.isAxiosError(err)) throw err
      const error = err as AuthFetchError
      console.error(err)
      enqueueErrorStack(t('dashboard.errors.powermeters-hardware-fetch'), error.code, error.message)
    }
  }

  const loadLocation = async (storageId: number): Promise<void> => {
    try {
      const response = await authFetch<StorageLocation>({ path: `storages/${storageId}/location` })
      if (response.status === StatusCodes.OK) {
        dispatch(setLocation(response.data))
      }
    } catch (err) {
      if (!axios.isAxiosError(err)) throw err
      const error = err as AuthFetchError
      console.error(err)
      enqueueErrorStack(t('dashboard.errors.storage-location-fetch'), error.code, error.message)
    }
  }

  const loadInstallation = async (installationId: number): Promise<void> => {
    try {
      const response = await authFetch<Installation>({
        path: `installations/${installationId}`,
        endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
      })
      if (response.status === StatusCodes.OK) {
        dispatch(setInstallation(response.data))
      }
    } catch (err) {
      if (!axios.isAxiosError(err)) throw err
      const error = err as AuthFetchError
      console.error(err)
      enqueueErrorStack(t('dashboard.errors.installation-fetch'), error.code, error.message)
    }
  }

  const loadActiveStorageBattery = async (installationId: number): Promise<void> => {
    const deviceLinks = await getDeviceLinks(installationId)
    if (deviceLinks.error) return
    const activeDeviceLinks = getActiveDeviceLinksOnly(deviceLinks.value as DeviceLinkModel[])

    if (activeDeviceLinks?.length) {
      dispatch(setActiveStorageBattery(activeDeviceLinks[0].deviceId ?? undefined))
    }
  }

  const loadBatteries = async (storageId: number): Promise<void> => {
    const batteryData = await authFetch<BatteryModel[]>({ path: `storages/${storageId}/hardware/batteries` })
      .then(response => {
        if (response.status === StatusCodes.OK) {
          return new ApiResult<BatteryModel[]>(response.data, false)
        }
        return new ApiResult<BatteryModel[]>(null, false)
      })
      .catch(err => {
        if (!axios.isAxiosError(err)) throw err
        const error = err as AuthFetchError
        console.error(err)
        enqueueErrorStack(t('dashboard.errors.batteries-hardware-fetch'), error.code, error.message)
        return new ApiResult<BatteryModel[]>(null, false)
      })

    dispatch(addBatteries(batteryData.value ?? []))
  }

  const loadStorage = async (storageId: number): Promise<void> => {
    const storageData = await authFetch<StorageDataModel>({ path: `storages/${storageId}` })
      .then(response => {
        if (response.status === StatusCodes.OK) {
          return new ApiResult<StorageDataModel>(response.data, false)
        }
        return new ApiResult<StorageDataModel>(null, false)
      })
      .catch(err => {
        const error = err as AuthFetchError
        console.error(err)
        enqueueErrorStack(t('dashboard.errors.storage-fetch'), error.code, error.message)
        return new ApiResult<StorageDataModel>(null, false)
      })

    if (!storageData.hasErrors && storageData.value) {
      dispatch(setStorageData(storageData.value))
    }
  }

  const loadStorageData = async (storageId: number, installationId: number): Promise<void> => {
    dispatch(setStorageLoading(true))
    await loadStorage(storageId)
    await loadInstallation(installationId)
    await loadActiveStorageBattery(installationId)
    await loadBatteries(storageId)
    dispatch(setStorageLoading(false))
  }

  return {
    loadStorageData,
    loadLocation,
    loadPowermeters,
    loadPowermetersFromClients,
    getDeviceLinks,
    isSubDevicesReady,
  }
}

export default useDashboardHelpers
